import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useLocanceGeoCheck, type GeoBlockReason } from '@/hooks/useLocanceGeoCheck';
import { GeoBlockScreen } from '@/components/locance/GeoBlockScreen';
import { GEO_ENABLED } from '@/lib/locance';
import { acquireGeoSession } from '@/lib/acquireGeoSession';
import { mapDepositError } from '@/lib/depositErrors';
import { eligibilityToGeoBlock } from '@/lib/geoEligibility';
import {
  UserPlus, Users, Trophy, Clock, DollarSign, Check, Gavel,
  LogIn, ChevronRight, AlertCircle, Loader2, Wallet,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { setPendingJoin, clearPendingJoin } from '@/lib/pendingJoin';
import {
  PrizeScheduleDisclosure,
  type PrizeScheduleRow,
  type PrizeScheduleState,
} from '@/components/nfl/PrizeScheduleDisclosure';

interface LeaguePreview {
  id: string;
  name: string;
  max_teams: number;
  filled_teams: number;
  draft_type: string;
  draft_status: string;
  draft_timer: number;
  entry_fee: number;
  prize_pool: number | null;
  season_year: number;
  status: string;
  commissioner_name: string;
  roster_config: Record<string, number> | null;
  // SAB-632 pre-join disclosure block from the nfl-join-league GET preview.
  prize_schedule: { state: PrizeScheduleState; rows: PrizeScheduleRow[] } | null;
}

export default function NFLJoinLeaguePage() {
  const { inviteCode: urlCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  // SAB-466 Locance geo gate — FAIL CLOSED for paid buy-ins.
  const geo = useLocanceGeoCheck();

  const [code, setCode] = useState(urlCode?.toUpperCase() || '');
  const [league, setLeague] = useState<LeaguePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [teamName, setTeamName] = useState('');
  // SAB-493: profile-eligibility block (declared state) → <GeoBlockScreen/>.
  const [eligibilityBlock, setEligibilityBlock] = useState<GeoBlockReason | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [joined, setJoined] = useState(false);
  const [joinedLeagueId, setJoinedLeagueId] = useState<string | null>(null);
  // SAB-596: already a member of this league → inline state, not the join form.
  const [alreadyMember, setAlreadyMember] = useState(false);
  // SAB-595: the user's wallet balance, read up front. null = free league or
  // not yet loaded; a number (0 for a missing wallet row) drives the shortfall UI.
  const [walletBalance, setWalletBalance] = useState<number | null>(null);

  // Auto-lookup when invite code is in URL
  useEffect(() => {
    if (urlCode) lookupLeague(urlCode);
  }, [urlCode]);

  // SAB-596: detect existing membership ON LOAD so we never show the join form
  // (and never the "Welcome! You're in!" success screen) to someone already in.
  useEffect(() => {
    if (!user || !league) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('nfl_league_members')
        .select('id')
        .eq('user_id', user.id)
        .eq('league_id', league.id)
        .maybeSingle();
      if (!cancelled && data) {
        setAlreadyMember(true);
        setJoinedLeagueId(league.id);
      }
    })();
    return () => { cancelled = true; };
  }, [user, league]);

  // SAB-595: read the user's wallet balance UP FRONT, so an insufficient balance
  // surfaces as a plain shortfall + "Add funds and join", never a raw
  // insufficient_funds error after the user presses Join. Real money is a single
  // per-user balance (user_wallets keyed on user_id, usable across any league); a
  // missing row (never funded) is $0, not an error.
  useEffect(() => {
    if (!user || !league || league.entry_fee <= 0) { setWalletBalance(null); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_wallets')
        .select('balance')
        .eq('user_id', user.id)
        .maybeSingle();
      if (!cancelled) setWalletBalance(Number((data as { balance?: number } | null)?.balance ?? 0));
    })();
    return () => { cancelled = true; };
  }, [user, league]);

  const lookupLeague = async (inviteCode: string) => {
    const normalized = inviteCode.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!normalized || normalized.length < 4) return;

    setLoading(true);
    setError(null);
    setLeague(null);

    try {
      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/nfl-join-league?invite_code=${normalized}`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      const data = await res.json();

      if (data.error) {
        setError(data.error === 'League not found' ? 'No league found with that code.' : data.error);
      } else {
        setLeague(data.league);
      }
    } catch {
      setError('Failed to look up league. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!user || !league) return;
    setJoining(true);

    try {
      // Paid buy-ins only: profile-eligibility (declared state, fail-OPEN) +
      // Locance geo (fail-CLOSED), before the join POST. Free leagues skip both.
      let geoSessionId: string | undefined; // SAB-600: forwarded to the server geo gate on a paid join.
      if (league.entry_fee > 0) {
        setEligibilityBlock(null);
        try {
          const { data: sess } = await supabase.auth.getSession();
          const eligRes = await fetch(
            `${(supabase as any).supabaseUrl}/functions/v1/check-eligibility`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${sess?.session?.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ action: 'get_user_status' }),
            }
          );
          const block = eligibilityToGeoBlock(await eligRes.json());
          if (block) {
            setEligibilityBlock(block);
            return;
          }
        } catch {
          // fail open — a down eligibility service must not block
        }

        // Locance geo. Gather under GEO_ENABLED OR GEO_OBSERVE (see acquireGeoSession).
        // ok:false only under GEO_ENABLED (enforce client) on a non-Compliant verdict:
        // aborts here and geo.geoBlock drives the existing GeoBlockScreen overlay. Under observe we send
        // the session and let the SERVER decide (no local block, no new UI).
        const geoRes = await acquireGeoSession(geo);
        if (!geoRes.ok) {
          return;
        }
        geoSessionId = geoRes.sessionId;
      }

      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) {
        toast({ title: 'Not logged in', variant: 'destructive' });
        return;
      }

      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/nfl-join-league`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            invite_code: code,
            team_name: teamName.trim() || undefined,
            ...(geoSessionId ? { geo_session_id: geoSessionId } : {}),
          }),
        }
      );

      const data = await res.json();

      if (data.error) {
        if (data.error === 'already_joined') {
          // SAB-596: not a success — render the plain inline "already a member"
          // state, not the "Welcome! You're in!" screen.
          setAlreadyMember(true);
          setJoinedLeagueId(data.league_id);
        } else if (data.error === 'insufficient_funds' || data.code === 'INSUFFICIENT_FUNDS') {
          // SAB-595: balance changed between the up-front read and the join (rare
          // race, or the funds path was skipped). Reflect the real balance so the
          // UI flips to the shortfall + "Add funds and join" — never a raw code.
          if (typeof data.available === 'number') setWalletBalance(data.available);
          toast({
            title: 'Add funds to join',
            description: data.message || `You need $${league.entry_fee} in your wallet to join.`,
          });
        } else {
          // TASK 1: route the rest through the shared money-action mapper, so a
          // GEO_BLOCKED shows the SAB-605 location copy (not the generic edge string)
          // and KYC/RG map correctly. already_joined + insufficient_funds handled above.
          const { title, message } = mapDepositError({ code: data.code, message: data.error, category: data.category, detected_state: data.detected_state });
          toast({ title, description: message, variant: 'destructive' });
        }
      } else {
        setJoined(true);
        setJoinedLeagueId(data.league.id);
        clearPendingJoin(); // SAB-595: consumed on a successful join — never resurface
        toast({ title: 'Joined!', description: `Welcome to ${data.league.name}!` });
      }
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setJoining(false);
    }
  };

  // SAB-595: declared state/age eligibility — cheap, fail-open. Run BEFORE sending
  // anyone to the deposit flow so we never take money from an obviously-ineligible
  // user. The authoritative Locance geo check is deferred to the actual join, where
  // the server gate enforces it (one location prompt, linear flow — no bouncing).
  const passesDeclaredEligibility = async (): Promise<boolean> => {
    try {
      const { data: sess } = await supabase.auth.getSession();
      const eligRes = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/check-eligibility`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${sess?.session?.access_token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'get_user_status' }),
        }
      );
      const block = eligibilityToGeoBlock(await eligRes.json());
      if (block) { setEligibilityBlock(block); return false; }
    } catch {
      // fail open — a down eligibility service must not block
    }
    return true;
  };

  // SAB-595: insufficient balance → fund first, then come back here. Stash the
  // invite code (localStorage, 1h TTL) so the deposit flow returns to this page,
  // and prefill the deposit to max(shortfall, $10 server minimum). No raw error:
  // the error is prevented, not handled.
  const handleAddFundsAndJoin = async () => {
    if (!user || !league) return;
    setEligibilityBlock(null);
    if (!(await passesDeclaredEligibility())) return;
    const shortfall = Math.max(0, league.entry_fee - (walletBalance ?? 0));
    const deposit = Math.max(shortfall, 10);
    setPendingJoin(code, league.id);
    navigate(
      `/wallet/deposit?league=${league.id}&amount=${deposit}&join=${encodeURIComponent(code)}&shortfall=${shortfall}`,
    );
  };

  const needsFunds =
    !!league && league.entry_fee > 0 && walletBalance !== null && walletBalance < league.entry_fee;

  const totalSlots = league?.roster_config
    ? Object.values(league.roster_config).reduce((a, b) => a + b, 0)
    : null;

  // ── Already a member (SAB-596) ── plain inline state: no join form, no join
  // button, no success iconography (nothing succeeded — they were already in).
  if (alreadyMember) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
        <Card className="bg-card border-border max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <h1 className="text-xl font-display font-bold text-foreground">
              You're already in {league?.name ?? 'this league'}
            </h1>
            <p className="text-sm text-muted-foreground">
              No need to join again — your spot is saved.
            </p>
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              onClick={() => navigate('/nfl')}
            >
              Go to League
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Success screen ── genuine fresh join only.
  if (joined) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
        <Card className="bg-card border-border max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <div className="h-16 w-16 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto">
              <Check className="h-8 w-8 text-green-400" />
            </div>
            <h1 className="text-xl font-display font-bold text-foreground">
              Welcome to {league?.name}!
            </h1>
            <p className="text-sm text-muted-foreground">
              You're in! Wait for the commissioner to start the draft.
            </p>
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              onClick={() => navigate('/nfl')}
            >
              Go to League
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4 py-8">
      {/* SAB-493 4c geo-block screen — profile-eligibility (declared) OR Locance
          (detected) layer for paid joins. Display-only; server gates authoritative. */}
      {(eligibilityBlock ?? (GEO_ENABLED ? geo.geoBlock : null)) && (
        <GeoBlockScreen
          reason={eligibilityBlock ?? (GEO_ENABLED ? geo.geoBlock : null)}
          busy={geo.state === 'prompting' || geo.state === 'checking'}
          onRetry={() => handleJoin()}
          onDismiss={() => {
            setEligibilityBlock(null);
            geo.reset();
          }}
        />
      )}
      <div className="max-w-md w-full space-y-4">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="h-14 w-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto">
            <UserPlus className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-xl font-display font-bold text-foreground">Join NFL League</h1>
          <p className="text-sm text-muted-foreground">
            Enter an invite code to find and join a league
          </p>
        </div>

        {/* Code input (shown if no URL code or to allow changing) */}
        {!league && (
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Invite code (e.g. ABC123)"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && lookupLeague(code)}
                  className="font-mono text-lg tracking-wider uppercase bg-muted/30"
                  maxLength={8}
                />
                <Button
                  onClick={() => lookupLeague(code)}
                  disabled={loading || code.length < 4}
                  className="bg-primary hover:bg-primary/90 shrink-0"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Find'}
                </Button>
              </div>
              {error && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="h-3.5 w-3.5 text-red-400 shrink-0" />
                  <p className="text-xs text-red-400">{error}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* League preview */}
        {loading && (
          <Card className="bg-card border-border">
            <CardContent className="p-8 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </CardContent>
          </Card>
        )}

        {league && (
          <>
            <Card className="bg-card border-border">
              <CardContent className="p-4 space-y-4">
                {/* League name + commissioner */}
                <div>
                  <h2 className="text-lg font-display font-bold text-foreground">{league.name}</h2>
                  <p className="text-xs text-muted-foreground">
                    Created by {league.commissioner_name} · {league.season_year} Season
                  </p>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-2 gap-2">
                  <StatBox
                    icon={<Users className="h-3.5 w-3.5" />}
                    label="Teams"
                    value={`${league.filled_teams}/${league.max_teams}`}
                  />
                  <StatBox
                    icon={<Gavel className="h-3.5 w-3.5" />}
                    label="Draft"
                    value={league.draft_type === 'snake' ? 'Snake (Live)' : 'Slow'}
                  />
                  <StatBox
                    icon={<Clock className="h-3.5 w-3.5" />}
                    label="Pick Timer"
                    value={league.draft_type === 'snake'
                      ? `${league.draft_timer}s`
                      : `${league.draft_timer / 3600}hr`
                    }
                  />
                  <StatBox
                    icon={<DollarSign className="h-3.5 w-3.5" />}
                    label="Buy-In"
                    value={league.entry_fee > 0 ? `$${league.entry_fee}` : 'Free'}
                  />
                </div>

                {/* SAB-632 pre-join disclosure: payout structure, before the join CTA */}
                {league.prize_schedule && (
                  <PrizeScheduleDisclosure
                    state={league.prize_schedule.state}
                    rows={league.prize_schedule.rows}
                    entryFee={league.entry_fee}
                    prizePool={league.prize_pool}
                  />
                )}

                {/* Roster config summary */}
                {totalSlots && (
                  <div className="p-2.5 rounded-lg bg-muted/30 border border-border">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold mb-1">Roster</p>
                    <p className="text-xs text-foreground">
                      {totalSlots} total slots
                      {league.roster_config && (
                        <span className="text-muted-foreground">
                          {' '}· {league.roster_config.QB || 1} QB, {league.roster_config.RB || 2} RB, {league.roster_config.WR || 2} WR, {league.roster_config.TE || 1} TE, {league.roster_config.FLEX || 1} FLEX
                        </span>
                      )}
                    </p>
                  </div>
                )}

                {/* Status badges */}
                <div className="flex gap-2">
                  <Badge variant="outline" className="text-[10px] border-border text-muted-foreground">
                    Sabotage Scoring
                  </Badge>
                  {league.filled_teams >= league.max_teams ? (
                    <Badge variant="outline" className="text-[10px] border-red-500/30 text-red-400">
                      Full
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] border-green-500/30 text-green-400">
                      {league.max_teams - league.filled_teams} spot{league.max_teams - league.filled_teams !== 1 ? 's' : ''} open
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Join section */}
            {league.filled_teams >= league.max_teams ? (
              <Card className="bg-card border-border">
                <CardContent className="p-4 text-center">
                  <AlertCircle className="h-5 w-5 text-red-400 mx-auto mb-2" />
                  <p className="text-sm text-foreground font-semibold">League Full</p>
                  <p className="text-xs text-muted-foreground mt-1">No spots remaining in this league.</p>
                </CardContent>
              </Card>
            ) : league.draft_status !== 'pending' ? (
              <Card className="bg-card border-border">
                <CardContent className="p-4 text-center">
                  <AlertCircle className="h-5 w-5 text-amber-400 mx-auto mb-2" />
                  <p className="text-sm text-foreground font-semibold">Draft Already Started</p>
                  <p className="text-xs text-muted-foreground mt-1">You can no longer join this league.</p>
                </CardContent>
              </Card>
            ) : !user ? (
              <Card className="bg-card border-border">
                <CardContent className="p-4 space-y-3">
                  {/* SAB-592: a new invitee is the primary case, so sign-up leads.
                      returnTo carries the invite through auth (and email confirmation)
                      so they land back here signed-in instead of being routed into
                      someone else's league by the landing gate. */}
                  <p className="text-sm text-foreground font-semibold text-center">Create an account to join</p>
                  <Button
                    className="w-full bg-primary hover:bg-primary/90"
                    onClick={() => navigate(`/signup?returnTo=${encodeURIComponent('/nfl/join/' + code)}`)}
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign Up to Join
                  </Button>
                  <p className="text-[10px] text-muted-foreground text-center">
                    Already have an account?{' '}
                    <button onClick={() => navigate(`/login?returnTo=${encodeURIComponent('/nfl/join/' + code)}`)} className="text-primary hover:underline">
                      Sign in
                    </button>
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-card border-border">
                <CardContent className="p-4 space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-foreground">Your Team Name</label>
                    <Input
                      placeholder={`${user.user_metadata?.full_name || 'My'}'s Team`}
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      maxLength={30}
                      className="bg-muted/30"
                    />
                  </div>
                  {needsFunds ? (
                    <>
                      {/* SAB-595: shortfall shown plainly BEFORE they act — no raw error. */}
                      <div className="flex items-start gap-2 p-2.5 rounded-lg bg-muted/30 border border-border">
                        <Wallet className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                        <p className="text-[11px] text-muted-foreground">
                          You need <span className="font-semibold text-foreground">${league.entry_fee}</span> to join.
                          Your wallet has <span className="font-semibold text-foreground">${(walletBalance ?? 0).toFixed(2)}</span>.
                        </p>
                      </div>
                      <Button
                        className="w-full bg-primary hover:bg-primary/90"
                        onClick={handleAddFundsAndJoin}
                        disabled={joining}
                      >
                        <Wallet className="h-4 w-4 mr-2" />
                        Add funds and join
                      </Button>
                    </>
                  ) : (
                    <>
                      {league.entry_fee > 0 && (
                        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/30 border border-border">
                          <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <p className="text-[10px] text-muted-foreground">
                            ${league.entry_fee} buy-in is charged from your wallet the moment you join — not at draft time
                          </p>
                        </div>
                      )}
                      <Button
                        className="w-full bg-primary hover:bg-primary/90"
                        onClick={handleJoin}
                        disabled={joining}
                      >
                        {joining ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <UserPlus className="h-4 w-4 mr-2" />
                        )}
                        Join League
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Change code link */}
            <button
              onClick={() => { setLeague(null); setError(null); }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center"
            >
              Try a different code
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function StatBox({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="p-2.5 rounded-lg bg-muted/30 border border-border text-center">
      <div className="flex items-center justify-center text-muted-foreground mb-1">{icon}</div>
      <p className="text-xs font-bold text-foreground">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}
